import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  IconButton,
  Select,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Badge,
  Divider,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import { DownloadIcon, DeleteIcon, AttachmentIcon } from '@chakra-ui/icons';
import tramitesService, { Tramite, Documento } from '../../../../services/tramitesObrasService';

interface ModalDocumentosProps {
  isOpen: boolean;
  onClose: () => void;
  tramite: Tramite;
  onSuccess: () => void;
}

const ModalDocumentos: React.FC<ModalDocumentosProps> = ({
  isOpen,
  onClose,
  tramite,
  onSuccess,
}) => {
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [idRequisito, setIdRequisito] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      cargarDocumentos();
    }
  }, [isOpen, tramite.id]);

  const cargarDocumentos = async () => {
    setLoading(true);
    try {
      const tramiteCompleto = await tramitesService.obtenerTramite(tramite.id);
      setDocumentos(tramiteCompleto.documentos || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los documentos',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: 'Error',
        description: 'Por favor seleccione un archivo',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setUploading(true);
    try {
      await tramitesService.subirDocumento(tramite.id, selectedFile, {
        idRequisito: idRequisito ? Number(idRequisito) : undefined,
        descripcion,
      });

      toast({
        title: 'Éxito',
        description: 'Documento subido correctamente',
        status: 'success',
        duration: 3000,
      });

      // Limpiar formulario
      setSelectedFile(null);
      setIdRequisito('');
      setDescripcion('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Recargar documentos
      cargarDocumentos();
      onSuccess();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Error al subir el documento',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentoId: number) => {
    if (!window.confirm('¿Está seguro de eliminar este documento?')) {
      return;
    }

    try {
      await tramitesService.eliminarDocumento(tramite.id, documentoId);
      toast({
        title: 'Éxito',
        description: 'Documento eliminado correctamente',
        status: 'success',
        duration: 3000,
      });
      cargarDocumentos();
      onSuccess();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Error al eliminar el documento',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDownload = (documentoId: number) => {
    const url = tramitesService.getUrlDescargaDocumento(tramite.id, documentoId);
    window.open(url, '_blank');
  };

  const formatFecha = (fecha: string) => {
    return new Date(fecha).toLocaleString('es-MX');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Documentos del Trámite {tramite.folio}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Sección de subida */}
            <Box bg="blue.50" p={4} borderRadius="md">
              <Text fontWeight="bold" mb={3}>
                Subir Nuevo Documento
              </Text>

              {tramite.tipoTramite && tramite.tipoTramite.id && (
                <FormControl mb={3}>
                  <FormLabel fontSize="sm">Requisito (Opcional)</FormLabel>
                  <Select
                    value={idRequisito}
                    onChange={(e) => setIdRequisito(e.target.value)}
                    placeholder="Seleccione un requisito"
                    size="sm"
                  >
                    {/* Aquí deberías cargar los requisitos del tipo de trámite */}
                    {/* Por simplicidad, lo dejamos así por ahora */}
                  </Select>
                </FormControl>
              )}

              <FormControl mb={3}>
                <FormLabel fontSize="sm">Descripción (Opcional)</FormLabel>
                <Input
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Descripción del documento"
                  size="sm"
                />
              </FormControl>

              <FormControl mb={3}>
                <FormLabel fontSize="sm">Archivo</FormLabel>
                <Input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  size="sm"
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Tipos permitidos: PDF, JPG, PNG, DOC, DOCX (Máx. 10MB)
                </Text>
              </FormControl>

              {selectedFile && (
                <Text fontSize="sm" color="blue.600" mb={2}>
                  Archivo seleccionado: {selectedFile.name}
                </Text>
              )}

              <Button
                colorScheme="blue"
                size="sm"
                leftIcon={<AttachmentIcon />}
                onClick={handleUpload}
                isLoading={uploading}
                isDisabled={!selectedFile}
              >
                Subir Documento
              </Button>
            </Box>

            <Divider />

            {/* Lista de documentos */}
            <Box>
              <Text fontWeight="bold" mb={3}>
                Documentos Adjuntos ({documentos.length})
              </Text>

              {loading ? (
                <Flex justify="center" p={4}>
                  <Spinner />
                </Flex>
              ) : documentos.length === 0 ? (
                <Text color="gray.500" textAlign="center" py={4}>
                  No hay documentos adjuntos
                </Text>
              ) : (
                <VStack spacing={2} align="stretch">
                  {documentos.map((doc) => (
                    <Box
                      key={doc.id}
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      borderWidth="1px"
                    >
                      <Flex justify="space-between" align="start">
                        <Box flex={1}>
                          <HStack spacing={2} mb={1}>
                            <Text fontWeight="bold" fontSize="sm">
                              {doc.nombreArchivo}
                            </Text>
                            {doc.esSolicitud && (
                              <Badge colorScheme="purple">Solicitud</Badge>
                            )}
                          </HStack>

                          {doc.descripcion && (
                            <Text fontSize="xs" color="gray.600" mb={1}>
                              {doc.descripcion}
                            </Text>
                          )}

                          {doc.requisito && (
                            <Text fontSize="xs" color="blue.600" mb={1}>
                              <strong>Requisito:</strong> {doc.requisito.nombreRequisito}
                            </Text>
                          )}

                          <Text fontSize="xs" color="gray.500">
                            Subido: {formatFecha(doc.fechaSubida)}
                            {doc.usuarioSubida && ` por ${doc.usuarioSubida}`}
                          </Text>
                        </Box>

                        <HStack>
                          <IconButton
                            aria-label="Descargar"
                            icon={<DownloadIcon />}
                            size="sm"
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => handleDownload(doc.id)}
                          />
                          <IconButton
                            aria-label="Eliminar"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleDelete(doc.id)}
                          />
                        </HStack>
                      </Flex>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalDocumentos;
